import React, { useCallback, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RuleLastModificationInfo, RuleType } from '@loyalty/shared-common/rule-api-client';
import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload';
import { faUpload } from '@fortawesome/pro-solid-svg-icons/faUpload';
import { useTranslation } from 'react-i18next';
import { RuleConflictModal, RuleConflictModalProps } from '../RuleConflictModal';
import { RuleExportModal } from './RuleExportModal';
import { RuleImportModal } from './RuleImportModal';
import { canImportExportRules, canUseRulesImportExport } from './rule-import-export-utils';
import { useFeatures } from '../../../features/FeaturesContext';
import { useRules } from '../../RulesContext';

export interface RuleImportExportButtonsProps {
  ruleType: 'EARNING_RULE' | 'BURNING_RULE';
}

export function RuleImportExportButtons({
  ruleType,
}: RuleImportExportButtonsProps): React.ReactElement | null {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { earningRules, burningRules, fetchRules } = useRules();

  const ruleApiType = ruleType === 'EARNING_RULE' ? RuleType.EARN : RuleType.BURN;
  const rules = ruleType === 'EARNING_RULE' ? earningRules : burningRules;

  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType: ruleApiType,
  });

  const gated = canUseRulesImportExport(features, ruleType);
  if (!gated) {
    return null;
  }

  const importExportAllowed = canImportExportRules(features, ruleType);

  const importExportTooltip = importExportAllowed ? '' : t('shared.action.permission.required');

  const refreshRules = useCallback(() => fetchRules(ruleApiType), [fetchRules, ruleApiType]);

  const handleConcurrencyConflict = useCallback((details: RuleLastModificationInfo) => {
    setConflictDialogProps(prev => ({
      ...prev,
      lastModification: details,
    }));
    setConflictModalIsOpen(true);
  }, []);

  return (
    <>
      <RuleConflictModal
        {...conflictDialogProps}
        ruleType={ruleApiType}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <RuleExportModal isOpen={exportOpen} onClose={() => setExportOpen(false)} rules={rules} />
      <RuleImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        ruleApiType={ruleApiType}
        rulesSnapshot={rules}
        onImported={refreshRules}
        onConcurrencyConflict={handleConcurrencyConflict}
      />
      <Tooltip title={importExportTooltip}>
        <span>
          <Button
            color="grey"
            variant="outlined"
            disabled={!importExportAllowed}
            startIcon={<FontAwesomeIcon icon={faDownload} />}
            onClick={() => setExportOpen(true)}
          >
            {t('rules.import-export.export.button')}
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={importExportTooltip}>
        <span>
          <Button
            color="grey"
            variant="outlined"
            disabled={!importExportAllowed}
            startIcon={<FontAwesomeIcon icon={faUpload} />}
            onClick={() => setImportOpen(true)}
          >
            {t('rules.import-export.import.button')}
          </Button>
        </span>
      </Tooltip>
    </>
  );
}
